import { useNavigate } from 'react-router-dom';
import { Terminal, Home, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';

const GLITCH_CHARS = '!@#$%^&*<>?/\\|ABCDEFGHIJKLMNabcdefghijklmn0123456789';

function GlitchText({ text }: { text: string }) {
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    let frame = 0;
    const interval = setInterval(() => {
      frame++;
      if (frame > 20) {
        setDisplay(text);
        clearInterval(interval);
        return;
      }
      setDisplay(
        text
          .split('')
          .map((char, i) =>
            i < frame / 2
              ? char
              : GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)],
          )
          .join(''),
      );
    }, 40);
    return () => clearInterval(interval);
  }, [text]);

  return <span>{display}</span>;
}

export default function NotFound() {
  const navigate = useNavigate();
  const [scanDone, setScanDone] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setScanDone(true), 1800);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-bot-base flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" />

      <div className="relative z-10 max-w-lg w-full mx-auto px-6 text-center">
        {/* Error code */}
        <div className="mb-2">
          <span className="text-xs text-cyber-red">
            // SYSTEM ERROR
          </span>
        </div>

        <h1 className="text-[6rem] font-bold leading-none text-brand-400 mb-0">
          <GlitchText text="404" />
        </h1>

        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="h-px flex-1 bg-cyber-red/30" />
          <span className="text-xs font-medium text-cyber-red">PAGE NOT FOUND</span>
          <div className="h-px flex-1 bg-cyber-red/30" />
        </div>

        {/* Terminal block */}
        <div className="panel text-left p-4 mb-6 font-mono text-xs rounded-lg">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-bot-mid">
            <Terminal size={11} className="text-brand-400" />
            <span className="text-xs font-medium text-brand-400">DIAGNOSTIC OUTPUT</span>
          </div>

          <div className="space-y-1 text-ink-mid">
            <p>
              <span className="text-cyber-green">{'>'}</span> scanning route table...
            </p>
            <p>
              <span className="text-cyber-green">{'>'}</span> checking agent registry...
            </p>
            <p>
              <span className="text-cyber-green">{'>'}</span> querying vector index...
            </p>
            {scanDone && (
              <>
                <p className="text-cyber-red mt-2">
                  <span className="text-cyber-red">!</span> ERROR 0x404: UNIT NOT DEPLOYED
                </p>
                <p className="text-ink-dim text-xs">
                  The requested resource does not exist in this cluster.
                </p>
              </>
            )}
            {!scanDone && (
              <p className="text-cyber-yellow animate-pulse">
                <span>{'>'}</span> running diagnostics
                <span className="inline-block w-1 h-3 bg-cyber-yellow ml-1 align-middle animate-pulse" />
              </p>
            )}
          </div>
        </div>

        {/* Status strip */}
        <div className="flex items-center justify-center gap-4 mb-8 text-xs text-ink-dim">
          <span className="flex items-center gap-1.5">
            <span className="led led-red" />
            ROUTE_OFFLINE
          </span>
          <span className="text-bot-mid">|</span>
          <span className="flex items-center gap-1.5">
            <span className="led led-green" />
            CORE_SYSTEMS_OK
          </span>
          <span className="text-bot-mid">|</span>
          <span className="flex items-center gap-1.5">
            <span className="led led-cyan" />
            AGENTS_ACTIVE
          </span>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <ArrowLeft size={13} />
            GO BACK
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary flex items-center justify-center gap-2"
          >
            <Home size={13} />
            COMMAND CENTER
          </button>
        </div>
      </div>
    </div>
  );
}
